import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { format } from "date-fns";
import { Loader2, LogOut, ArrowLeft, Copy, Mail, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { hasAdminAccess } from "@/lib/admin-access";
import { listIncompleteCheckouts, type IncompleteUser } from "@/lib/admin-students.functions";
import { buildWhatsAppUrl } from "@/lib/phone";
import { checkoutFollowUpMessage } from "@/lib/checkout-followup";

export const Route = createFileRoute("/admin/incomplete-checkouts")({
  head: () => ({
    meta: [
      { title: "Incomplete Checkouts - Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminIncompleteCheckoutsPage,
});

function AdminIncompleteCheckoutsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [users, setUsers] = useState<IncompleteUser[]>([]);
  const listFn = useServerFn(listIncompleteCheckouts);

  const load = useCallback(async () => {
    try {
      const res = await listFn();
      setUsers(res.users);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load incomplete checkouts");
    }
  }, [listFn]);

  useEffect(() => {
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        navigate({ to: "/auth", search: { redirect: "/admin/incomplete-checkouts" } });
        return;
      }
      if (!(await hasAdminAccess(sess.session))) {
        setAuthorized(false);
        setLoading(false);
        return;
      }
      setAuthorized(true);
      await load();
      setLoading(false);
    })();
  }, [navigate, load]);

  const copyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success("Email copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  if (loading)
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  if (!authorized)
    return (
      <div className="min-h-[60vh] grid place-items-center px-5">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl">Not authorized</h1>
        </div>
      </div>
    );

  return (
    <section className="mx-auto max-w-5xl px-5 py-12">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to admin
      </Link>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-primary uppercase tracking-wider">Admin</p>
          <h1 className="mt-1 font-display text-4xl font-semibold">Incomplete Checkouts</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {users.length} signed-up user{users.length === 1 ? "" : "s"} without a successful payment.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={async () => {
            await supabase.auth.signOut();
            navigate({ to: "/auth" });
          }}
          className="gap-2"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>

      <div className="mt-8 grid gap-3">
        {users.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
            Nobody is stuck mid-checkout right now. 🎉
          </div>
        )}
        {users.map((u) => (
          <article
            key={u.id}
            className="rounded-2xl border border-border bg-card p-5 flex flex-wrap items-center justify-between gap-3"
          >
            <div className="min-w-0">
              <p className="font-display text-lg font-semibold">{u.name ?? "-"}</p>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {u.email && (
                  <a href={`mailto:${u.email}`} className="inline-flex items-center gap-1 hover:text-primary">
                    <Mail className="h-3.5 w-3.5" /> {u.email}
                  </a>
                )}
                <span>Joined {format(new Date(u.created_at), "PP")}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {u.phone && buildWhatsAppUrl(u.phone, "") ? (
                <a
                  href={buildWhatsAppUrl(u.phone, checkoutFollowUpMessage(u.name, u.last_plan))}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp follow-up
                </a>
              ) : (
                <span className="self-center text-xs text-muted-foreground">No phone on file</span>
              )}
              {u.email && (
                <Button variant="outline" size="sm" onClick={() => copyEmail(u.email!)} className="gap-1.5">
                  <Copy className="h-4 w-4" /> Copy Email
                </Button>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
